import { Component, ChangeDetectionStrategy, Input, OnInit, Output, EventEmitter, TemplateRef, ViewChild } from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SelectOption } from '@shared/components/multi-select/models';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { cloneDeep } from 'lodash';
import { ConnectedPosition } from "@angular/cdk/overlay";

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class MultiSelectComponent implements OnInit, IDestroy {
  private _isMobile = false;
  private _dontEmitValue = false;
  private _options: SelectOption[] = [];
  private _prevOptions: UntypedFormArray;
  private _dialogRef: MatDialogRef<TemplateRef<any>>;
  public componentDestroy: () => Subject<any>;
  public isOpenDropDown = false;
  public chooseAll = false;
  public selectForm = new UntypedFormGroup({
    options: new UntypedFormArray([])
  });
  public optionsFormArray = this.selectForm.get('options') as UntypedFormArray;
  public icons = CustomIcons;

  public get options(): SelectOption[] {
    return this._options;
  }

  @Input()
  public set options(value: SelectOption[]) {
    if (value && this._options !== value) {
      this._options = value;
      this.optionsFormArray.clear();
      this._dontEmitValue = true;
      this._options?.forEach((option, index) => {
        this.optionsFormArray.push(new UntypedFormControl(option.isSelected));
        if (index === this._options.length - 1) {
          this._dontEmitValue = false;
        }
      });
    }
  }
  @Output() public readonly isOpenDropDownChanged = new EventEmitter<boolean>();
  @Input() public overlayPosition: ConnectedPosition[] = [];
  @Input() public chooseAllText = 'SHARED_COMPONENTS.SELECT_ALL';
  @Input() public dropdownMaxHeight = 200;
  @Input() public dropdownWidth = 314;
  @Input() public buttonWidth = 140;
  @Input() public inputTitle: string;
  @Input() public isVersionImpaired = false;
  @Output() public readonly choose = new EventEmitter<boolean[]>();
  @ViewChild('dropDownModalTemp') public dropDownModalTemp: TemplateRef<any>;

  public get isIndeterminate(): boolean {
    return this.chooseAll ? false : this.optionsFormArray.value.some((isChosen: boolean) => isChosen);
  }

  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
    tap(state => {
      this._isMobile = state.breakpoints['(max-width: 768px)'];
    })
  );

  constructor(private breakpointObserver: BreakpointObserver, private dialog: MatDialog) {
    this.optionsFormArray.valueChanges.pipe(takeUntil(this.componentDestroy())).subscribe((value: boolean[]) => {
      this.chooseAll = value.every((isChosen: boolean) => isChosen);
      if (!this._dontEmitValue) {
        this.choose.emit(value);
      }
    });
  }

  private dropDownChanged(): void {
    this.isOpenDropDown = !this.isOpenDropDown;
    this.isOpenDropDownChanged.emit(this.isOpenDropDown);
  }

  public ngOnInit(): void {
    if (this.optionsFormArray.length === 0) {
      this._dontEmitValue = true;
      this._options.forEach((op, index) => {
        if (index === this._options.length - 1) {
          this._dontEmitValue = false;
        }
        this.optionsFormArray.push(new UntypedFormControl(op.isSelected));
      });
    }
  }

  public setOptionsEqualValue(isChosen: boolean): void {
    this.optionsFormArray.setValue(Array(this.options?.length).fill(isChosen));
  }

  public choseAllOptions(isShow: boolean): void {
    this.chooseAll = isShow;
    this.setOptionsEqualValue(isShow);
  }

  public closeModal(): void {
    if (this._dialogRef) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.optionsFormArray.setValue(this._prevOptions.value);
      this._dialogRef.close();
    }
  }

  public openDropDown(): void {
    if (this._isMobile) {
      const config = {
        maxWidth: '100%',
        width: '100%',
        position: {
          bottom: '0px'
        },
        backdropClass: 'backdrop',
        hasBackdrop: true,
        panelClass: 'mobile-bottom-show-up-small-dialog'
      };
      this._prevOptions = cloneDeep(this.optionsFormArray);
      this._dialogRef = this.dialog.open(this.dropDownModalTemp, config);
    } else {
      this.dropDownChanged();
    }
  }

  public backDropClick(): void {
    this.dropDownChanged();
  }

  public apply(): void {
    if (this._dialogRef) {
      this._dialogRef.close();
    }
  }
}
